/*
 * Copyright (c) CIRROM 2025.
 */

import React from "react";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import Tooltip from "@mui/material/Tooltip";
import { CardHeader } from "@mui/material";
import { HelpOutlined } from "@mui/icons-material";
import AccountBoxIcon from "@mui/icons-material/AccountBox";
import BusinessIcon from "@mui/icons-material/Business";
import EmailIcon from "@mui/icons-material/Email";
import DraftsIcon from "@mui/icons-material/Drafts";
import { deepOrange, teal } from "@mui/material/colors";
import { MiraCard, MiraCardContent } from "../mira/components/common/Card";
import { IntegratedSystemDetail } from "../../api/integratedsystems";
import { NUMBER_FORMATTER } from "../utils/utils";

interface CRMOverviewListItemProps {
  fact: number;
  header: string;
  tooltip: string;
  icon: React.ReactNode;
  iconColor?: string;
}

function CRMOverviewListItem({
  fact,
  header,
  tooltip,
  icon,
  iconColor,
}: Readonly<CRMOverviewListItemProps>) {
  return (
    <ListItem disableGutters sx={{ py: 0.5 }}>
      <div style={{ display: "flex", width: "100%" }}>
        <div
          style={{
            width: "30px",
            height: "30px",
            marginRight: "5px",
            alignSelf: "center",
            color: iconColor,
          }}
        >
          {icon}
        </div>

        <Divider orientation="vertical" flexItem />

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            paddingLeft: "10px",
            flex: 1,
          }}
        >
          <div>
            <Typography variant="body2" sx={{ fontWeight: "bold" }}>
              {header}
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontWeight: "bold", color: "#777776" }}
            >
              {NUMBER_FORMATTER.format(fact)}
            </Typography>
          </div>

          <Tooltip title={tooltip}>
            <HelpOutlined style={{ width: "15px", height: "15px" }} />
          </Tooltip>
        </div>
      </div>
    </ListItem>
  );
}

interface CRMOverviewPanelProps {
  systemDetails: IntegratedSystemDetail;
}

export const CRMOverviewPanel: React.FC<CRMOverviewPanelProps> = ({
  systemDetails,
}) => {
  if (!systemDetails?.crm_stats) {
    return null;
  }

  const uniqueContacts =
    systemDetails.crm_stats.total_contacts > 0
      ? systemDetails.crm_stats.total_contacts -
        (systemDetails.merged_contacts?.tickets || 0)
      : 0;

  return (
    <MiraCard sx={{ height: "100%" }}>
      <CardHeader title="CRM Overview" />
      <MiraCardContent>
        <List dense>
          <CRMOverviewListItem
            fact={systemDetails.crm_stats.total_contacts}
            header="Total Contacts"
            tooltip="Total contacts available in your CRM"
            icon={<AccountBoxIcon style={{ width: "30px", height: "30px" }} />}
          />
          <CRMOverviewListItem
            fact={uniqueContacts}
            header="Unique Contacts"
            tooltip="Total Contacts less duplicates"
            icon={<AccountBoxIcon style={{ width: "30px", height: "30px" }} />}
            iconColor={teal[800]}
          />
          <CRMOverviewListItem
            fact={systemDetails.crm_stats.orphaned_contacts}
            header="Orphaned Contacts"
            tooltip="Contacts in your CRM that have no Account"
            icon={<AccountBoxIcon style={{ width: "30px", height: "30px" }} />}
            iconColor={deepOrange[600]}
          />
          <CRMOverviewListItem
            fact={systemDetails.crm_stats.companies_count?.crm || 0}
            header="Total Companies"
            tooltip="Total Accounts available in your CRM"
            icon={<BusinessIcon style={{ width: "30px", height: "30px" }} />}
          />
          <CRMOverviewListItem
            fact={systemDetails.crm_stats.companies_count?.cirrom || 0}
            header="Unique Companies"
            tooltip="Accounts Cirrom considers to be unique. It could be lower (eg. duplicate company names) or higher (eg. multiple domains associated with the same account) than the CRM account number"
            icon={<BusinessIcon style={{ width: "30px", height: "30px" }} />}
            iconColor={teal[800]}
          />
          {systemDetails.email_stats &&
          systemDetails.email_stats.sent_emails > 0 ? (
            <CRMOverviewListItem
              fact={systemDetails.email_stats.sent_emails}
              header="Sent Email"
              tooltip="Sent emails in the last 90 days"
              icon={<EmailIcon style={{ width: "30px", height: "30px" }} />}
            />
          ) : null}
          {systemDetails.email_stats &&
          systemDetails.email_stats.opened_emails > 0 ? (
            <CRMOverviewListItem
              fact={systemDetails.email_stats.opened_emails}
              header="Opened Email"
              tooltip="Opened emails in the last 90 days"
              icon={<DraftsIcon style={{ width: "30px", height: "30px" }} />}
              iconColor={teal[800]}
            />
          ) : null}
        </List>
      </MiraCardContent>
    </MiraCard>
  );
};
